import { type ClientDispatchedToolResult } from 'api-validations/aurora'
import { isNonNil } from 'utils'

import { type AsAppliedLayer, effectiveFilters, getDataTools, type LayerSelection } from '../map'
import { type Stream } from '../operation-wide-data'
import type { Cff } from '../operation-wide-data/browsableFields'
import {
  type CurrentlyViewing,
  useAuroraRegistrationStore,
  type ViewedLayer
} from './auroraRegistrationStore'

export const toCurrentlyViewing = ({
  selection,
  cff,
  stream
}: {
  selection: LayerSelection | null
  cff: Cff | null
  stream: Stream | undefined
}): CurrentlyViewing | undefined => {
  if (selection === null || cff === null) return undefined
  if (selection.kind !== 'AsApplied') return { ...cff, layer: toViewedLayer(selection) }
  if (stream === undefined) return undefined
  return {
    ...cff,
    layer: {
      kind: 'AsApplied',
      streamId: selection.streamId,
      applicationId: selection.applicationId,
      layerId: selection.layerId,
      season: stream.season,
      crop: stream.cropLabel
    }
  }
}

const toViewedLayer = (selection: Exclude<LayerSelection, AsAppliedLayer>): ViewedLayer => {
  switch (selection.kind) {
    case 'Field':
    case 'PlanEditor':
    case 'BoundaryCreator':
    case 'PrescriptionCreator':
      return { kind: selection.kind }
    case 'Planning':
    case 'PlanAssetEditor':
      return { kind: selection.kind, assetType: selection.assetType }
    case 'Satellite':
      return {
        kind: selection.kind,
        imagery: selection.imagery,
        captureDate: selection.captureDate
      }
    case 'SoilSample':
      return { kind: selection.kind, measurement: selection.measurement }
    case 'SoilSurvey':
      return { kind: selection.kind, attribute: selection.attribute }
  }
}

export const describeCurrentView = ({
  isOnMap
}: {
  isOnMap: boolean
}): ClientDispatchedToolResult<'DescribeCurrentView'> => {
  const { currentlyViewing } = useAuroraRegistrationStore.getState().contextSlots
  // Report filters and the lasso only while the user can see them on the map.
  const dataTools = isOnMap ? getDataTools() : undefined
  const filters =
    dataTools === undefined
      ? []
      : effectiveFilters(dataTools.filters).map((filter) => ({
          streamId: filter.streamId,
          applicationId: filter.applicationId,
          layerId: filter.layerId,
          type: filter.type,
          range: filter.range
        }))
  const lasso = isNonNil(dataTools?.lasso)
    ? {
        mode: dataTools.lassoMode,
        active: dataTools.lassoActive,
        selectedAreaAcres: dataTools.lasso.selectedArea.value,
        geojson: dataTools.lasso.collection
      }
    : null
  return {
    viewing: currentlyViewing ?? null,
    url: `${window.location.pathname}${window.location.search}`,
    filters,
    lasso
  }
}
